import { useState, useEffect } from 'react';

/**
 * Hook لحساب ارتفاع الهيدر بدقة في الوقت الفعلي
 * يحدث تلقائياً عند تغيير حجم الشاشة أو تحديث الهيدر
 */
export function useHeaderHeight() {
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const calculateHeaderHeight = () => {
      // البحث عن الهيدر باستخدام selectors مختلفة
      const headerSelectors = [
        'header',
        '[data-header]',
        '.header',
        'nav',
        '.navigation'
      ];

      let headerElement: HTMLElement | null = null;
      
      for (const selector of headerSelectors) {
        headerElement = document.querySelector(selector);
        if (headerElement) break;
      }

      if (headerElement) {
        const rect = headerElement.getBoundingClientRect();
        const computedHeight = rect.height;
        setHeaderHeight(Math.max(computedHeight, 60)); // حد أدنى 60px
      } else {
        // في حالة عدم وجود هيدر، استخدم القيمة الافتراضية
        setHeaderHeight(64);
      }
    };

    // حساب فوري عند التحميل
    calculateHeaderHeight();

    // إعادة حساب عند تغيير حجم النافذة
    const handleResize = () => {
      setTimeout(calculateHeaderHeight, 100); // debounce قصير
    };

    // مراقبة تغييرات DOM للهيدر
    const observer = new MutationObserver(calculateHeaderHeight);
    
    const headerElement = document.querySelector('header');
    if (headerElement) {
      observer.observe(headerElement, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      observer.disconnect();
    };
  }, []);

  return headerHeight;
}

/**
 * Hook لحساب المساحة المتاحة للمحتوى
 */
export function useAvailableViewport() {
  const headerHeight = useHeaderHeight();
  const [viewportInfo, setViewportInfo] = useState({
    availableHeight: 0,
    viewportHeight: 0,
    safeAreaTop: 0,
    safeAreaBottom: 0
  });

  useEffect(() => {
    const calculateViewport = () => {
      const viewportHeight = window.innerHeight;
      const safeAreaTop = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue('env(safe-area-inset-top)') || '0'
      );
      const safeAreaBottom = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue('env(safe-area-inset-bottom)') || '0'
      );

      const availableHeight = viewportHeight - headerHeight - safeAreaTop - safeAreaBottom;

      setViewportInfo({
        availableHeight: Math.max(availableHeight, 300), // حد أدنى 300px
        viewportHeight,
        safeAreaTop,
        safeAreaBottom
      });
    };

    calculateViewport();

    const handleResize = () => {
      setTimeout(calculateViewport, 50);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [headerHeight]);

  return {
    headerHeight,
    ...viewportInfo
  };
}